import React, { useContext, useEffect } from 'react';
import { Route, Switch } from 'react-router-dom';
import pluralize from 'helpers/pluralize';
import delve from 'dlv';
import { connect } from 'react-redux';

import Message from 'components/base/Message/Message';
import Layout from 'components/styled/Layout/Layout';

import Track from './Track';
import Release from './Release';
import Artist from './Artist';
import Label from './Label';
import Project from './Project';

import { EntityContext } from 'context/entityContext';
import topics from 'topics';
import { useFilterUrlHandlers } from 'components/TopicFilters/utils/useFilterUrlHandlers';
import { useTopicFilters } from 'components/TopicFilters/utils/useTopicFilters';

interface IEntityContentProps {
  isExternalOneLabel: boolean;
  loading: boolean;
  artistMetadata?: any;
}
/**
 * EntityContent returns the topic navigation and loads the correct topic (overview/streaming/etc...) in the template
 * Right now it also contains the logic for the compare version, though that might need to be split up in a different file for maintainability
 */
const EntityContent: React.FC<IEntityContentProps> = ({
  isExternalOneLabel,
  loading,
  artistMetadata,
}) => {
  const { compareMode, entityType, entityIds, countryIds } =
    useContext(EntityContext);

  let showEntityContent = true;

  let pathPrefix = '/entity';
  const compareType = compareMode === 'country' ? compareMode : entityType;
  let compareItemCount;

  const [filters] = useTopicFilters();
  const [, clearFilterUrl] = useFilterUrlHandlers(filters, ['country']);

  useEffect(() => {
    if (compareMode === 'country' && (filters.region || filters.customRegion)) {
      clearFilterUrl(['region', 'customRegion']);
    }
    if (
      compareMode === 'country' &&
      filters.country &&
      filters.country.includes('EX-')
    ) {
      clearFilterUrl('country');
    }
  }, [filters, clearFilterUrl, compareMode]);

  if (compareMode) {
    if (compareMode === 'entity') {
      compareItemCount = entityIds.length;
      pathPrefix = '/compare/entity';
    } else if (compareMode === 'country') {
      compareItemCount = countryIds?.length;
      pathPrefix = `/compare/country/${
        compareItemCount && compareItemCount > 0 ? countryIds?.join(',') : 'all'
      }/entity`;
    }

    showEntityContent = !!compareItemCount && compareItemCount > 1;
  }

  const topicTypes = topics.join('|'); // Available topic names

  return (
    <>
      {!showEntityContent && (
        <Layout.MaxWidth>
          <Message>
            {`You need at least 2 ${pluralize(
              compareType
            )} to show comparison data.`}
          </Message>
        </Layout.MaxWidth>
      )}

      {showEntityContent && (
        <Switch>
          <Route
            path={`${pathPrefix}/track/:entityId/:topic(${topicTypes})`}
            render={(renderProps): JSX.Element => (
              <Track
                pathPrefix={pathPrefix}
                {...renderProps}
                loading={loading}
                artistMetadata={artistMetadata}
              />
            )}
          />

          <Route
            path={`${pathPrefix}/track/:parentEntityId/version/:entityId/:topic(${topicTypes})`}
            render={(renderProps): JSX.Element => (
              <Track
                pathPrefix={pathPrefix}
                {...renderProps}
                loading={loading}
                artistMetadata={artistMetadata}
              />
            )}
          />

          <Route
            path={`${pathPrefix}/release/:entityId/:topic(${topicTypes})`}
            render={(renderProps): JSX.Element => (
              <Release
                pathPrefix={pathPrefix}
                {...renderProps}
                loading={loading}
                artistMetadata={artistMetadata}
              />
            )}
          />

          <Route
            path={`${pathPrefix}/release/:parentEntityId/version/:entityId/:topic(${topicTypes})`}
            render={(renderProps): JSX.Element => (
              <Release
                pathPrefix={pathPrefix}
                {...renderProps}
                loading={loading}
                artistMetadata={artistMetadata}
              />
            )}
          />

          <Route
            path={`${pathPrefix}/artist/:entityId/:topic(${topicTypes})`}
            render={(renderProps): JSX.Element => (
              <Artist pathPrefix={pathPrefix} {...renderProps} />
            )}
          />
          {!isExternalOneLabel && (
            <Route
              path={`${pathPrefix}/label/:entityId`}
              render={(): JSX.Element => <Label />}
            />
          )}
          <Route
            path={`${pathPrefix}/project/:entityId/:topic(${topicTypes})`}
            render={(renderProps): JSX.Element => (
              <Project
                pathPrefix={pathPrefix}
                {...renderProps}
                loading={loading}
                artistMetadata={artistMetadata}
              />
            )}
          />
        </Switch>
      )}
    </>
  );
};

export default connect((state) => ({
  isExternalOneLabel: delve(state, ['authentication', 'isExternalOneLabel']),
}))(EntityContent);
